import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import BlockIcon from "@mui/icons-material/Block";
import CandBMonogramNavy from "src/icons/CandBMonogramNavy";
import api from "src/store/features/api";
import type {
  AssessmentQuestionDto,
  SubmitAssessmentResponseDto,
} from "src/store/features/api/generated";
import {
  UKRiskSummary,
  UKInvestorType,
  UKRestrictedInvestorStatement,
  UKHNWInvestorStatement,
} from "src/components/application/uk-onboarding";

const SUPPORT_ARTICLE_URL =
  "https://calebandbrown.com/en-gb/uk-cryptoasset-investing";

const formatDate = (timestamp?: number | null) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const InvalidLinkNotice = () => (
  <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
    <Typography variant="h6" fontWeight={600} gutterBottom>
      This link is expired or invalid
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Please reach out to the{" "}
      <Link href="https://www.calebandbrown.com/contact/" target="_blank" rel="noopener">
        Caleb &amp; Brown support
      </Link>{" "}
      team for
      a new link.
    </Typography>
  </Paper>
);

const UKPublicAssessment = () => {
  const code = new URLSearchParams(window.location.search).get("code");

  const [validateToken] = api.useValidatePublicUkAssessmentTokenMutation();
  const [getQuestions, { isLoading: isLoadingQuestions }] =
    api.useGetPublicUkAssessmentQuestionsMutation();
  const [submitAssessment, { isLoading: isSubmitting }] =
    api.useSubmitPublicUkAssessmentMutation();

  const [tokenState, setTokenState] = useState<"checking" | "valid" | "invalid">("checking");
  const [firstName, setFirstName] = useState<string | null>(null);
  // Pre-assessment classification journey, mirroring the authenticated flow:
  // risk summary -> investor type -> investor statement -> assessment.
  const [stage, setStage] = useState<"risk" | "investorType" | "statement" | "assessment">(
    "risk",
  );
  const [investorType, setInvestorType] = useState<"RESTRICTED" | "HIGH_NET_WORTH" | null>(null);
  const [statementAgreed, setStatementAgreed] = useState(false);
  const [questions, setQuestions] = useState<AssessmentQuestionDto[]>([]);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [lockoutEndsAt, setLockoutEndsAt] = useState<number | undefined>(undefined);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SubmitAssessmentResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadQuestions = async () => {
    if (!code) return;
    setResult(null);
    setAnswers({});
    setCurrentQuestionIndex(0);
    setBlockedMessage(null);
    setError(null);
    try {
      const res = await getQuestions({ code }).unwrap();
      if (!res.success) {
        setBlockedMessage(res.message || "The assessment is not available right now.");
        setLockoutEndsAt(res.lockoutEndsAt);
        return;
      }
      setQuestions(res.questions);
      setAttemptNumber(res.attemptNumber);
    } catch (err: any) {
      setBlockedMessage(
        err?.data?.message ||
          "Something went wrong loading the assessment. Please try again later.",
      );
    }
  };

  useEffect(() => {
    if (!code) {
      setTokenState("invalid");
      return;
    }
    validateToken({ code })
      .unwrap()
      .then((res) => {
        setTokenState(res.valid ? "valid" : "invalid");
        setFirstName(res.firstName ?? null);
      })
      .catch(() => setTokenState("invalid"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    // Only fetch questions once the user has worked through the classification
    // steps and reached the assessment stage.
    if (tokenState === "valid" && stage === "assessment") loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenState, stage]);

  const handleSubmit = async () => {
    if (!code) return;
    setError(null);
    const answerList = questions.map((q) => ({
      questionId: q.id,
      selectedOptionId: answers[q.id],
    }));
    try {
      const res = await submitAssessment({
        code,
        answers: answerList,
        investorType: investorType as "RESTRICTED" | "HIGH_NET_WORTH",
        statementAgreed,
      }).unwrap();
      setResult(res);
    } catch (err: any) {
      setError(err?.data?.message || "Failed to submit your answers. Please try again.");
    }
  };

  const renderBody = () => {
    if (tokenState === "invalid") return <InvalidLinkNotice />;

    if (tokenState === "checking") {
      return (
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            Loading...
          </Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Paper>
      );
    }

    // Pre-assessment classification steps (Risk Summary -> Investor Type ->
    // Investor Statement), mirroring the authenticated onboarding flow minus
    // the Onfido / application-form steps.
    if (stage === "risk") {
      return (
        <UKRiskSummary
          onPublicContinue={() => setStage("investorType")}
          publicFirstName={firstName}
        />
      );
    }
    if (stage === "investorType") {
      return (
        <UKInvestorType
          onPublicContinue={(type) => {
            setInvestorType(type);
            setStage("statement");
          }}
          onPublicBack={() => setStage("risk")}
        />
      );
    }
    if (stage === "statement") {
      return investorType === "HIGH_NET_WORTH" ? (
        <UKHNWInvestorStatement
          onPublicContinue={() => {
            setStatementAgreed(true);
            setStage("assessment");
          }}
          onPublicBack={() => setStage("investorType")}
        />
      ) : (
        <UKRestrictedInvestorStatement
          onPublicProceed={() => {
            setStatementAgreed(true);
            setStage("assessment");
          }}
          onPublicBack={() => setStage("investorType")}
        />
      );
    }

    if (isLoadingQuestions) {
      return (
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            Loading Assessment...
          </Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Paper>
      );
    }

    if (blockedMessage) {
      return (
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
          <BlockIcon sx={{ fontSize: 64, color: "#F44336", mb: 2 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Assessment Unavailable
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {blockedMessage}
          </Typography>
          {lockoutEndsAt && (
            <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
              You can try again after: <strong>{formatDate(lockoutEndsAt)}</strong>
            </Alert>
          )}
        </Paper>
      );
    }

    if (result) {
      // The manual-review band (score 80-94) clears the assessment gate
      // too — the review happens in the background.
      if (result.passed || result.manualReviewRequired) {
        return (
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: "#E8F5E9",
              border: "2px solid #4CAF50",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "#4CAF50", mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              You have passed the assessment
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              {result.message}
            </Typography>
          </Paper>
        );
      }

      const canRetry = !result.permanentLockout && result.remainingAttempts > 0;
      return (
        <Stack spacing={3}>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: result.permanentLockout ? "#FFEBEE" : "#FFF3E0",
              border: result.permanentLockout ? "2px solid #F44336" : "2px solid #FF9800",
            }}
          >
            {result.permanentLockout ? (
              <BlockIcon sx={{ fontSize: 64, color: "#F44336", mb: 2 }} />
            ) : (
              <CancelOutlinedIcon sx={{ fontSize: 64, color: "#FF9800", mb: 2 }} />
            )}
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Assessment Not Passed
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {result.message}
            </Typography>
            {result.permanentLockout && result.lockoutEndsAt && (
              <Alert severity="error" sx={{ mt: 2, textAlign: "left" }}>
                You can re-apply after: <strong>{formatDate(result.lockoutEndsAt)}</strong>
              </Alert>
            )}
            {result.waitRequired && result.nextAttemptAvailableAt && (
              <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
                You can try again after:{" "}
                <strong>{formatDate(result.nextAttemptAvailableAt)}</strong>
              </Alert>
            )}
          </Paper>

          {canRetry && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                What Can You Do?
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body1">
                  You have <strong>{result.remainingAttempts}</strong> attempt(s) remaining.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "14px" }}>
                  We recommend reviewing this{" "}
                  <Link
                    href={SUPPORT_ARTICLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    fontWeight={700}
                  >
                    support article
                  </Link>{" "}
                  to learn more about the cryptocurrency asset class, the UK regulatory
                  environment and Caleb &amp; Brown before attempting the test again.
                </Typography>
                {!result.waitRequired && (
                  <Stack direction="row" justifyContent="center">
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={loadQuestions}
                      sx={{ minWidth: 200 }}
                    >
                      Retry Assessment
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      );
    }

    if (questions.length === 0) return null;

    const currentQuestion = questions[currentQuestionIndex];
    const totalQuestions = questions.length;
    const selectedAnswer = answers[currentQuestion.id];
    const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
    const allAnswered = questions.every((q) => !!answers[q.id]);

    return (
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={600}>
              Appropriateness Assessment
            </Typography>
            <Chip
              label={`Attempt ${attemptNumber} of 3`}
              color={attemptNumber >= 3 ? "warning" : "default"}
              size="small"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Please answer the following questions to help us assess your understanding of
            cryptoassets.
          </Typography>
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}% Complete
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={500} gutterBottom>
            {currentQuestion.questionText}
          </Typography>
          <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
            <RadioGroup
              value={selectedAnswer || ""}
              onChange={(e) =>
                setAnswers((prev) => ({ ...prev, [currentQuestion.id]: e.target.value }))
              }
            >
              {currentQuestion.options.map((option) => (
                <Paper
                  key={option.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1,
                    border:
                      selectedAnswer === option.id ? "2px solid #1976d2" : "1px solid #e0e0e0",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { borderColor: "#1976d2", backgroundColor: "#f5f5f5" },
                  }}
                  onClick={() =>
                    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: option.id }))
                  }
                >
                  <FormControlLabel
                    value={option.id}
                    control={<Radio />}
                    label={option.text}
                    sx={{ m: 0, width: "100%" }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}
        {isSubmitting && <LinearProgress />}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between">
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={() => setCurrentQuestionIndex((i) => Math.max(0, i - 1))}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </Button>
          {isLastQuestion ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={!allAnswered || isSubmitting}
              sx={{ minWidth: 200 }}
            >
              {isSubmitting ? "Submitting..." : "Submit Assessment"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() =>
                setCurrentQuestionIndex((i) => Math.min(totalQuestions - 1, i + 1))
              }
              disabled={!selectedAnswer}
            >
              Next
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} sx={{ padding: "40px 20px 0" }}>
        <CandBMonogramNavy height={"32"} width={"28"} />
      </Grid>
      <Grid item xs={12} sm={10} md={8} lg={6} sx={{ p: { xs: 2, sm: 3 }, mb: 6 }}>
        {renderBody()}
      </Grid>
    </Grid>
  );
};

export default UKPublicAssessment;
